import {createSelector} from 'reselect'
import debounce from 'lodash/debounce.js'
import get from 'lodash/get.js'
import {isAfter, isBefore} from 'date-fns'

import {setForm, formsSelector, getState} from '../store.js'
import {fetchAllAPI} from '../common/fetchAll.js'
import {debouncedSaveUserSettings, userSettingSelector} from './me.js'
import {currentDateSelector} from '../ordoro/CurrentDateListener.js'

export const APP_MESSAGES = 'app_messages'
export const DISMISSED_APP_MESSAGE_IDS = 'dismissed_app_message_ids'

export function appMessagesSelector(state) {
  return formsSelector(state)[APP_MESSAGES] || appMessagesSelector.default
}
appMessagesSelector.default = []

export function appMessagesHaveLoadedSelector(state) {
  return !!formsSelector(state)[APP_MESSAGES]
}

export const bannerMessagesSelector = createSelector(
  appMessagesSelector,
  currentDateSelector,
  (appMessages, currentDate) =>
    appMessages.filter(
      ({meta}) =>
        get(meta, 'type', 'banner') === 'banner' &&
        (!meta.start_date_time ||
          isAfter(currentDate, new Date(meta.start_date_time))) &&
        (!meta.end_date_time ||
          isBefore(currentDate, new Date(meta.end_date_time))),
    ),
)

export const undismissableBannerMessagesSelector = createSelector(
  bannerMessagesSelector,
  (bannerMessages) =>
    bannerMessages.filter((message) =>
      get(message, 'meta.can_not_dismiss', false),
    ),
)

export const dismissableBannerMessagesSelector = createSelector(
  bannerMessagesSelector,
  (bannerMessages) =>
    bannerMessages.filter(
      (message) => !get(message, 'meta.can_not_dismiss', false),
    ),
)

export function dismissedAppMessageIDsSelector(state) {
  return userSettingSelector(state, {
    setting: DISMISSED_APP_MESSAGE_IDS,
    defaultValue: [],
  })
}

export const notDismissedBannerMessagesSelector = createSelector(
  dismissedAppMessageIDsSelector,
  dismissableBannerMessagesSelector,
  (dismissedAppMessageIDs, bannerMessages) =>
    bannerMessages.filter(
      (bannerMessage) => !dismissedAppMessageIDs.includes(bannerMessage.id),
    ),
)

export const bannerMessageSelector = createSelector(
  notDismissedBannerMessagesSelector,
  (bannerMessages) => bannerMessages[0],
)

export const showBannerMessageSelector = createSelector(
  bannerMessageSelector,
  (bannerMessage) => !!bannerMessage,
)

export const undismissableBannerMessageSelector = createSelector(
  undismissableBannerMessagesSelector,
  (bannerMessages) => bannerMessages[0],
)

export const showUndismissableBannerMessageSelector = createSelector(
  undismissableBannerMessageSelector,
  (bannerMessage) => !!bannerMessage,
)

export function setAppMessages(appMessages) {
  setForm(APP_MESSAGES, appMessages)
}

export async function getAppMessages() {
  const appMessages = await fetchAllAPI('/app_message/', 'app_message')

  setAppMessages(appMessages)
}

export function dismissAppMessage(appMessageID) {
  const dismissedAppMessageIDs = dismissedAppMessageIDsSelector(getState())

  let newIDs = dismissedAppMessageIDs.filter((id) => id !== appMessageID)

  newIDs.push(appMessageID)

  newIDs = newIDs.slice(-20)

  debouncedSaveUserSettings({
    [DISMISSED_APP_MESSAGE_IDS]: newIDs,
  })
}

export const getAppMessagesDebounced = debounce(getAppMessages, 500)
